import { Color3, Material, MeshBuilder, StandardMaterial, Vector4, VideoTexture, type Scene, type TransformNode } from "babylonjs";
import CreatorElement from "./creator.element";
import { initAlphaVideoShaderMaterial } from "../../content/core/content.common";
import { IAssetVideo, loadVideo } from "../loader/loader";
import { CreatorFile, CreatorSceneChild } from "./types";
import { IS_BABYLON_NATIVE_JSCORE } from "../utils/utils";

// A video panel: a unit plane scaled by the element's [w,h,1], with an opaque or
// side-by-side alpha-video material.
//
// Video element ownership: when no element is injected, the panel creates + owns
// its own <video> (forces muted autoplay, pauses on dispose). When an element is
// injected (from a VideoPool), the pool owns it — the panel only builds the
// VideoTexture on it and must NOT pause/clear the element on dispose (the pool
// reuses it and disposes it on pool.dispose()).
export default class CreatorVideoElement extends CreatorElement {
  private hasAlpha: boolean;
  private videoTexture?: VideoTexture;
  private injectedVideo?: HTMLVideoElement;
  private ownsVideoElement: boolean;

  constructor(
    scene: Scene,
    parent: TransformNode,
    child: CreatorSceneChild,
    file: CreatorFile,
    hasAlpha: boolean,
    videoElement?: HTMLVideoElement,
  ) {
    super(scene, parent, child, file);
    this.hasAlpha = hasAlpha;
    this.injectedVideo = videoElement;
    this.ownsVideoElement = !videoElement;
  }

  // The backing <video> element (exposed for first-frame / readyState checks).
  public get videoElement(): HTMLVideoElement | undefined {
    return this.videoTexture?.video ?? this.injectedVideo;
  }

  public async build(): Promise<void> {
    // When a pooled element is injected, loadVideo uses it as the VideoTexture
    // source (sets its src) instead of creating a fresh one.
    const asset: IAssetVideo = {
      type: "video",
      path: this.file.path,
      id: this.sceneID,
      videoElement: this.injectedVideo,
    };
    await loadVideo(asset, this.scene);
    const videoTexture = asset.videoTexture;
    if (!videoTexture) return;
    this.videoTexture = videoTexture;

    const plane = MeshBuilder.CreatePlane(`${this.sceneID}_video`, { size: 1 }, this.scene);
    plane.parent = this.anchor;

    let material: Material;
    if (this.hasAlpha) {
      // side-by-side colour|alpha video; U flipped via the swapped range box.
      material = initAlphaVideoShaderMaterial(
        this.scene,
        videoTexture,
        new Vector4(1.0, 0.0, 0.0, 1.0), // range (U flipped)
        new Vector4(0.0, 0.0, 0.5, 1.0), // colour = left half
        new Vector4(0.5, 0.0, 1.0, 1.0), // alpha = right half
      );
    } else {
      const sm = new StandardMaterial(`${this.sceneID}_vmat`, this.scene);
      videoTexture.uScale = -1; // flip U (horizontal).
      sm.diffuseTexture = videoTexture;
      sm.emissiveTexture = videoTexture;
      sm.emissiveColor = new Color3(1, 1, 1);
      sm.disableLighting = true;
      sm.backFaceCulling = false;
      material = sm;
    }
    plane.material = material;

    this.applyTransform(); // anchor carries position + rotation + scale ([w,h,1]).

    const video = videoTexture.video;
    if (this.ownsVideoElement) {
      // self-owned: muted autoplay/loop (muted is required for unattended autoplay).
      video.loop = true;
      video.muted = true;
      void video.play().catch(() => { /* gesture-gated; ignore */ });
    } else if (IS_BABYLON_NATIVE_JSCORE) {
      // pooled + Babylon Native: there is no pointer-unlock flow on native (the
      // VideoPool's autoplay-unlock is gated on window pointerdown, which never
      // fires), and the native HTMLVideoElement does not honour the `autoplay`
      // attribute — it only starts on an explicit play(). Kick it off here.
      video.loop = true;
      void video.play().catch(() => { /* ignore */ });
    }
    // pooled (web): the VideoPool owns mute/unlock/autoplay/loop — don't override.
  }

  // Show/hide also drives playback (pause when hidden).
  public override setEnabled(visible: boolean): void {
    super.setEnabled(visible);
    const video = this.videoElement;
    if (!video) return;
    if (visible) void video.play().catch(() => { /* ignore */ });
    else if (this.ownsVideoElement) video.pause();
  }

  public override dispose(): void {
    // Pause only an element we own; a pooled element is the pool's to manage.
    if (this.ownsVideoElement) {
      try { this.videoTexture?.video?.pause(); } catch { /* noop */ }
    }
    // The VideoTexture (GPU) is disposed by anchor.dispose() via the material;
    // VideoTexture.dispose does NOT clear the <video> src, so a pooled element
    // survives for the pool to reuse/dispose.
    this.videoTexture = undefined;
    super.dispose();
  }
}
